;(function () {
  function getMediaTarget(button) {
    var dm = button.closest('deferred-media')
    if (!dm) return null
    var iframe = dm.querySelector('iframe.js-vimeo')
    if (iframe) return { kind: 'vimeo', el: iframe, dm: dm }
    var video = dm.querySelector('video.js-html5-video')
    if (video) return { kind: 'html5', el: video, dm: dm }
    return null
  }

  function setVideoToggleState(button, playing) {
    button.setAttribute('aria-pressed', playing ? 'true' : 'false')

    var baseLabel = button.getAttribute('data-video-title') || 'video'
    button.setAttribute(
      'aria-label',
      (playing ? 'Pause' : 'Play') + ': ' + baseLabel
    )

    button.classList.toggle('is-playing', playing)
  }

  function getPlayer(target) {
    if (target.kind !== 'vimeo') return null
    if (typeof Vimeo === 'undefined') return null
    if (!target.el._player) {
      target.el._player = new Vimeo.Player(target.el)
    }
    return target.el._player
  }

  function playTarget(target) {
    if (target.kind === 'vimeo') {
      var player = getPlayer(target)
      if (!player) return
      player.play().catch(function () {})
    } else {
      var video = target.el
      if (!video.paused) return
      var p = video.play()
      if (p && typeof p.catch === 'function') p.catch(function () {})
    }
  }

  function pauseTarget(target) {
    if (target.kind === 'vimeo') {
      var player = getPlayer(target)
      if (!player) return
      player.pause()
    } else {
      var video = target.el
      if (!video.paused) video.pause()
    }
  }

  function targetFromEl(el) {
    var dm = el.closest('deferred-media')
    if (!dm) return null
    if (el.classList.contains('js-vimeo')) return { kind: 'vimeo', el: el, dm: dm }
    if (el.classList.contains('js-html5-video')) return { kind: 'html5', el: el, dm: dm }
    return null
  }

  // Tracks which media the shopper has manually paused, keyed by
  // `viewType:mediaId` (e.g. `PDP:123`, `Gallery:123`) so the main gallery and
  // its zoomed-modal clone — which share the same `data-media-id` — persist
  // pause intent independently. The `data-user-paused` attribute lives only on
  // the <video>/<iframe> element, so it is lost when a variant change rebuilds
  // the gallery (or zoomed) DOM. This Set survives that rebuild so the pause
  // intent can be re-applied to the freshly created element.
  var userPausedMedia = new Set()

  function mediaIdFor(elOrButton) {
    var dm = elOrButton.closest('deferred-media')
    return dm ? dm.getAttribute('data-media-id') : null
  }

  // Only the main PDP gallery persists pause intent. Zoomed-modal toggles use
  // their own resume mechanism (data-zoom-was-playing), so leave them alone.
  function isMainGalleryToggle(button) {
    return (
      !button.hasAttribute('data-zoomed-toggle') && !button.closest('modal-dialog')
    )
  }

  // ---------------------------------------------------------------------------
  // pdp_video_view tracking
  //
  // Measures *intentional* watch time and pushes `pdp_video_view` dataLayer
  // events per (video, type). Time only accrues while the video is both playing
  // AND ≥50% visible AND the tab is foregrounded. A (video, type) only qualifies
  // to emit after DWELL_MS of *continuous* active watching, which filters out
  // quick scroll-throughs.
  //
  // Emits are INCREMENTAL: each flush (tab hide, page exit, color change) pushes
  // only the time watched *since the previous flush* and zeroes the running
  // counter. So a video the shopper returns to after switching tabs yields
  // multiple disjoint events; the total watch time is the SUM of
  // `pdp_video_time_watched` across all events for that video+type. This avoids
  // losing re-watch time (a single capped event would) while never
  // double-counting — the emitted chunks never overlap.
  // ---------------------------------------------------------------------------
  var DWELL_MS = 2000

  // Watch sessions, keyed `type:mediaId` so a PDP video and its zoomed gallery
  // clone (same media id) track independently. `accumulatedMs` holds only the
  // un-emitted time (reset to 0 after each flush). Module-scoped like
  // userPausedMedia so a session survives a variant-driven gallery rebuild.
  var watchSessions = new Map()

  // Per-element live state, rebuilt whenever an element is (re)initialised.
  var watchState = new Map()

  function viewTypeFor(elOrButton) {
    return isMainGalleryToggle(elOrButton) ? 'PDP' : 'Gallery'
  }

  // Key for `userPausedMedia`. Combining view type with media id keeps the main
  // gallery and the zoomed clone (same media id) tracking independently.
  function pauseKeyFor(elOrButton) {
    var id = mediaIdFor(elOrButton)
    return id ? viewTypeFor(elOrButton) + ':' + id : null
  }

  function sessionFor(el) {
    var id = mediaIdFor(el)
    if (!id) return null
    var key = viewTypeFor(el) + ':' + id
    var session = watchSessions.get(key)
    if (!session) {
      session = {
        type: viewTypeFor(el),
        accumulatedMs: 0,
        qualified: false,
        styleColor: null,
      }
      watchSessions.set(key, session)
    }
    return session
  }

  function buttonFor(el) {
    var dm = el.closest('deferred-media')
    return dm ? dm.querySelector('.video-toggle') : null
  }

  function isActive(state) {
    return (
      state.playing &&
      state.visible &&
      !state.modalHidden &&
      document.visibilityState === 'visible'
    )
  }

  // A zoomed-gallery video lives inside a <modal-dialog> that, when closed, is
  // hidden only via visibility:hidden/opacity:0 (see component-dialog.css) — it
  // keeps display:block and stays full-viewport, so the IntersectionObserver
  // still reports the clone ≥50% visible. Without this gate, watch time would
  // keep accruing while the gallery is closed (and even before it's first
  // opened, since the clone muted-autoplays). `modalHidden` is true whenever the
  // containing zoom modal is closed: seeded at init from the modal's open state
  // and flipped by the modal open/close handlers below.
  function isInClosedZoomModal(el) {
    var modal = el.closest && el.closest('modal-dialog')
    if (!modal) return false
    if ((modal.id || '').indexOf('zoom-gallery') === -1) return false
    return !modal.hasAttribute('open')
  }

  // Reconcile an element's segment timer with its current active state.
  function recompute(el) {
    var state = watchState.get(el)
    if (!state) return
    var session = sessionFor(el)
    if (!session) return
    // Cache while attached — removal/flush can't re-derive it from a detached node.
    state.session = session

    var active = isActive(state)

    if (active && state.segmentStart == null) {
      // Active watch begins — open a segment and (if not yet qualified) arm the
      // continuous-dwell timer.
      state.segmentStart = performance.now()
      if (!session.qualified && state.dwellTimer == null) {
        state.dwellTimer = setTimeout(function () {
          session.qualified = true
          var button = buttonFor(el)
          if (button) {
            session.styleColor = button.dataset.eventStyleColor || null
          }
        }, DWELL_MS)
      }
    } else if (!active && state.segmentStart != null) {
      // Active watch ends — bank the elapsed time and break the dwell streak.
      session.accumulatedMs += performance.now() - state.segmentStart
      state.segmentStart = null
      if (state.dwellTimer != null) {
        clearTimeout(state.dwellTimer)
        state.dwellTimer = null
      }
    }
  }

  function watchStateFor(el) {
    var state = watchState.get(el)
    if (!state) {
      state = {
        playing: false,
        visible: false,
        modalHidden: isInClosedZoomModal(el),
        segmentStart: null,
        dwellTimer: null,
      }
      watchState.set(el, state)
    }
    return state
  }

  function setWatchPlaying(el, playing) {
    watchStateFor(el).playing = playing
    recompute(el)
  }

  function setWatchVisible(el, visible) {
    watchStateFor(el).visible = visible
    recompute(el)
  }

  function setWatchModalHidden(el, hidden) {
    watchStateFor(el).modalHidden = hidden
    recompute(el)
  }

  // Close every open segment (e.g. on tab hide) so banked time is current.
  function closeAllSegments() {
    watchState.forEach(function (s) {
      if (s && s.segmentStart != null) {
        if (s.session) s.session.accumulatedMs += performance.now() - s.segmentStart
        s.segmentStart = null
        if (s.dwellTimer != null) {
          clearTimeout(s.dwellTimer)
          s.dwellTimer = null
        }
      }
    })
  }

  // Push the un-emitted watch time for a session as one pdp_video_view, then
  // zero the counter so the next flush only reports newly watched time. The
  // `accumulatedMs <= 0` guard makes this a no-op when there's nothing new,
  // which is what stops the visibilitychange→hidden then pagehide sequence from
  // double-emitting (the second call sees a freshly reset counter). `qualified`
  // is intentionally NOT reset: the DWELL_MS dwell only gates the first emit;
  // once a video has qualified, subsequent chunks count even if short.
  function emitSession(session) {
    if (!session.qualified || session.accumulatedMs <= 0) return
    window.dataLayer.push({
      event: 'pdp_video_view',
      pdp_video_type: session.type,
      pdp_video_time_watched: Math.round(session.accumulatedMs / 100) / 10,
      event_style_color: session.styleColor,
    })
    session.accumulatedMs = 0
  }

  // Emit the pending watch-time chunk for each qualified session (tab hide /
  // page exit flush).
  function flushWatchSessions() {
    closeAllSegments()
    window.dataLayer = window.dataLayer || []
    watchSessions.forEach(emitSession)
  }

  // Emit then discard all sessions. Called on a color swatch change: the gallery
  // is rebuilt with different media, so we flush the pending watch-time chunk for
  // the outgoing color now (a pdp_video_view each for PDP and Gallery) and clear
  // the sessions. Clearing is what makes returning to a color start a fresh count
  // instead of resuming the previous color's session. The live watchState refs
  // are reset so the next recompute() rebuilds a brand-new session.
  function flushAndResetSessions() {
    closeAllSegments()
    window.dataLayer = window.dataLayer || []
    watchSessions.forEach(emitSession)
    watchSessions.clear()
    watchState.forEach(function (s) {
      s.session = null
      s.segmentStart = null
      if (s.dwellTimer != null) {
        clearTimeout(s.dwellTimer)
        s.dwellTimer = null
      }
    })
  }

  // Shared IntersectionObserver — plays a video when ≥50% visible, pauses it
  // otherwise. Respects the data-user-paused flag set by manual pause clicks.
  var io = new IntersectionObserver(
    function (entries) {
      // Sort so the most-visible entry is processed last and "wins" after
      // pauseAll() runs.
      entries
        .slice()
        .sort(function (a, b) {
          return a.intersectionRatio - b.intersectionRatio
        })
        .forEach(function (entry) {
          var target = targetFromEl(entry.target)
          if (!target) return

          setWatchVisible(entry.target, entry.intersectionRatio >= 0.5)

          if (entry.intersectionRatio >= 0.5) {
            if (entry.target.dataset.userPaused === '1') return
            pauseAll()
            playTarget(target)
          } else {
            pauseTarget(target)
          }
        })
    },
    { threshold: [0, 0.5] }
  )

  var INSET_PX = 16 // padding inside the video area

  function positionZoomedToggle(button, videoAspect) {
    var dm = button.closest('deferred-media')
    if (!dm) return

    var cw = dm.offsetWidth
    var ch = dm.offsetHeight
    if (!cw || !ch) return

    var containerAspect = cw / ch
    var videoW, videoH

    if (containerAspect > videoAspect) {
      // container is wider than video — bars on sides
      videoH = ch
      videoW = ch * videoAspect
    } else {
      // container is taller than video — bars top/bottom
      videoW = cw
      videoH = cw / videoAspect
    }

    var barX = (cw - videoW) / 2
    var barY = (ch - videoH) / 2

    button.style.bottom = barY + INSET_PX + 'px'
    button.style.left = barX + INSET_PX + 'px'
  }

  function observeZoomedAspect(button, aspect) {
    positionZoomedToggle(button, aspect)
    new ResizeObserver(function () {
      positionZoomedToggle(button, aspect)
    }).observe(button.closest('deferred-media'))
  }

  function initVimeoToggle(button, target) {
    var player = new Vimeo.Player(target.el)
    target.el._player = player

    player.on('playing', function () {
      // Autoplay can resume a rebuilt iframe the shopper had paused; re-pause.
      if (target.el.dataset.userPaused === '1') {
        player.pause()
        return
      }
      var posterOverlay = target.dm.querySelector('[data-vimeo-poster]')
      if (posterOverlay) {
        posterOverlay.style.opacity = '0'
        posterOverlay.style.pointerEvents = 'none'
      }
      setVideoToggleState(button, true)
      setWatchPlaying(target.el, true)
    })

    player.on('pause', function () {
      setVideoToggleState(button, false)
      setWatchPlaying(target.el, false)
    })

    player.on('ended', function () {
      setWatchPlaying(target.el, false)
    })

    player.ready().then(function () {
      if (target.el.dataset.userPaused === '1') {
        player.pause()
        setVideoToggleState(button, false)
        return
      }
      setVideoToggleState(button, true)
    })

    if (button.hasAttribute('data-zoomed-toggle')) {
      Promise.all([player.getVideoWidth(), player.getVideoHeight()]).then(
        function (dims) {
          observeZoomedAspect(button, dims[0] / dims[1])
        }
      )
    }
  }

  function initHtml5Toggle(button, target) {
    var video = target.el

    video.addEventListener('play', function () {
      // Native autoplay can emit 'play' shortly after a rebuilt video is wired
      // up. If the shopper had paused this media, re-pause instead of resuming.
      if (video.dataset.userPaused === '1') {
        video.pause()
        return
      }
      setVideoToggleState(button, true)
      setWatchPlaying(video, true)
    })

    video.addEventListener('pause', function () {
      setVideoToggleState(button, false)
      setWatchPlaying(video, false)
    })

    video.addEventListener('ended', function () {
      setWatchPlaying(video, false)
    })

    // Initial state reflects whether autoplay is already running.
    setVideoToggleState(button, !video.paused)
    setWatchPlaying(video, !video.paused)

    if (button.hasAttribute('data-zoomed-toggle')) {
      var applyAspect = function () {
        if (!video.videoWidth || !video.videoHeight) return
        observeZoomedAspect(button, video.videoWidth / video.videoHeight)
      }
      if (video.readyState >= 1) {
        applyAspect()
      } else {
        video.addEventListener('loadedmetadata', applyAspect, { once: true })
      }
    }
  }

  function initPlayToggles() {
    document
      .querySelectorAll('.video-toggle:not([data-toggle-init])')
      .forEach(function (button) {
        var target = getMediaTarget(button)
        if (!target) return

        // For Vimeo we need the SDK before we can wire up the player.
        if (target.kind === 'vimeo' && typeof Vimeo === 'undefined') return

        button.dataset.toggleInit = '1'
        io.observe(target.el)

        // Stop pointer/touch events from reaching Swiper so tapping the
        // button doesn't also open the zoomed gallery modal.
        ;['pointerdown', 'touchstart'].forEach(function (evt) {
          button.addEventListener(evt, function (e) {
            e.stopPropagation()
          })
        })

        if (target.kind === 'vimeo') {
          initVimeoToggle(button, target)
        } else {
          initHtml5Toggle(button, target)
        }

        // Re-apply persisted pause intent to a freshly rebuilt element (main
        // gallery or zoomed clone). This runs synchronously inside the
        // MutationObserver callback, before the IntersectionObserver's async
        // callback fires, so the flag is in place in time to suppress auto-play.
        if (userPausedMedia.has(pauseKeyFor(button))) {
          target.el.dataset.userPaused = '1'
          pauseTarget(target)
          setVideoToggleState(button, false)
        }
      })
  }

  function pauseAll() {
    document.querySelectorAll('iframe.js-vimeo').forEach(function (iframe) {
      if (typeof Vimeo === 'undefined') return
      if (!iframe._player) {
        iframe._player = new Vimeo.Player(iframe)
      }
      iframe._player.pause()
    })

    document.querySelectorAll('video.js-html5-video').forEach(function (video) {
      if (!video.paused) video.pause()
    })

    document
      .querySelectorAll('.video-toggle.is-playing')
      .forEach(function (btn) {
        setVideoToggleState(btn, false)
      })
  }

  document.addEventListener('DOMContentLoaded', function () {
    initPlayToggles()

    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.removedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return
          var removed = node.matches && node.matches('.js-html5-video, iframe.js-vimeo')
            ? [node]
            : node.querySelectorAll
              ? node.querySelectorAll('.js-html5-video, iframe.js-vimeo')
              : []
          removed.forEach && removed.forEach(function (el) {
            io.unobserve(el)
            // Bank any in-progress watch time before the element disappears,
            // then drop its per-element state. The session (keyed by
            // type:mediaId) survives so a rebuilt element keeps accruing.
            var s = watchState.get(el)
            if (s) {
              if (s.segmentStart != null && s.session) {
                s.session.accumulatedMs += performance.now() - s.segmentStart
              }
              if (s.dwellTimer != null) clearTimeout(s.dwellTimer)
              watchState.delete(el)
            }
          })
        })
      })
      initPlayToggles()
    }).observe(document.body, {
      childList: true,
      subtree: true,
    })
  })

  // Persist/clear the shopper's pause intent so it survives a gallery rebuild.
  // Applies to both the main gallery and the zoomed clone, keyed by view type.
  function recordPauseIntent(button, paused) {
    var key = pauseKeyFor(button)
    if (!key) return
    paused ? userPausedMedia.add(key) : userPausedMedia.delete(key)
  }

  function pushToggleEvent(button, playing) {
    window.dataLayer = window.dataLayer || []
    var payload = {
      event: playing ? 'pdp_video_play_click' : 'pdp_video_pause_click',
    }
    if (button.dataset.eventStyleColor) {
      payload.event_style_color = button.dataset.eventStyleColor
    }
    window.dataLayer.push(payload)
  }

  document.addEventListener(
    'click',
    function (e) {
      var button = e.target.closest('.video-toggle')
      if (!button) return

      var target = getMediaTarget(button)
      if (!target) return

      e.preventDefault()
      e.stopPropagation()

      if (target.kind === 'vimeo') {
        if (typeof Vimeo === 'undefined') return
        var iframe = target.el
        if (!iframe._player) {
          iframe._player = new Vimeo.Player(iframe)
        }
        iframe._player.getPaused().then(function (paused) {
          if (paused) {
            pauseAll()
            delete iframe.dataset.userPaused
            recordPauseIntent(button, false)
            iframe._player.play()
            setVideoToggleState(button, true)
            pushToggleEvent(button, true)
          } else {
            iframe.dataset.userPaused = '1'
            recordPauseIntent(button, true)
            iframe._player.pause()
            setVideoToggleState(button, false)
            pushToggleEvent(button, false)
          }
        })
      } else {
        var video = target.el
        if (video.paused) {
          pauseAll()
          delete video.dataset.userPaused
          recordPauseIntent(button, false)
          var p = video.play()
          if (p && typeof p.then === 'function') {
            p.then(function () {
              setVideoToggleState(button, true)
            }).catch(function () {
              setVideoToggleState(button, false)
            })
          } else {
            setVideoToggleState(button, true)
          }
          pushToggleEvent(button, true)
        } else {
          video.dataset.userPaused = '1'
          recordPauseIntent(button, true)
          video.pause()
          setVideoToggleState(button, false)
          pushToggleEvent(button, false)
        }
      }
    },
    true
  )

  // When the PDP zoomed gallery modal opens, pause any underlying gallery
  // videos that are currently playing and flag them so we can resume on
  // close. The zoomed clone lives inside the modal, so scope by
  // `!modal.contains(el)` to leave it alone. We deliberately do NOT set
  // `data-user-paused` — the IntersectionObserver should still resume play
  // when the modal closes and the underlying video returns to view.
  function isZoomGalleryEvent(event) {
    return typeof event?.detail === 'string' && event.detail.indexOf('zoom-gallery') !== -1
  }

  function getModalFromEvent(event) {
    return document.getElementById(event.detail)
  }

  document.addEventListener('modal-dialog::open', function (event) {
    if (!isZoomGalleryEvent(event)) return
    var modal = getModalFromEvent(event)

    document.querySelectorAll('video.js-html5-video').forEach(function (video) {
      if (modal && modal.contains(video)) return
      if (video.paused) return
      var target = targetFromEl(video)
      if (!target) return
      video.dataset.zoomWasPlaying = '1'
      pauseTarget(target)
    })

    document.querySelectorAll('iframe.js-vimeo').forEach(function (iframe) {
      if (modal && modal.contains(iframe)) return
      if (typeof Vimeo === 'undefined') return
      if (!iframe._player) {
        iframe._player = new Vimeo.Player(iframe)
      }
      iframe._player.getPaused().then(function (paused) {
        if (paused) return
        iframe.dataset.zoomWasPlaying = '1'
        iframe._player.pause()
      })
    })

    // The gallery is now visible — let its clones' watch time accrue again.
    // (The IntersectionObserver can't see the modal's visibility-based hide,
    // so accrual is gated explicitly on modal open/close instead.)
    if (modal) {
      modal
        .querySelectorAll('video.js-html5-video, iframe.js-vimeo')
        .forEach(function (el) {
          setWatchModalHidden(el, false)
        })
    }
  })

  document.addEventListener('modal-dialog::close', function (event) {
    if (!isZoomGalleryEvent(event)) return
    var modal = getModalFromEvent(event)

    document
      .querySelectorAll('[data-zoom-was-playing="1"]')
      .forEach(function (el) {
        if (modal && modal.contains(el)) return
        var target = targetFromEl(el)
        delete el.dataset.zoomWasPlaying
        if (!target) return
        playTarget(target)
      })

    // Closing the gallery hides the modal via visibility/opacity (not display),
    // so the IntersectionObserver never marks the clones out-of-view. Gate their
    // watch time off now: this banks the in-progress segment and stops accrual
    // while the gallery is closed; it resumes (into the same session) on reopen.
    if (modal) {
      modal
        .querySelectorAll('video.js-html5-video, iframe.js-vimeo')
        .forEach(function (el) {
          setWatchModalHidden(el, true)
        })
    }
  })

  // Tab visibility gates active watch time; hiding the tab is also a reliable
  // (mobile-safe) signal to flush accumulated pdp_video_view events. pagehide
  // is the terminal backstop for a full navigation/close.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      flushWatchSessions()
    } else {
      // Returning to the tab: reopen segments for anything still playing+visible.
      watchState.forEach(function (_state, el) {
        recompute(el)
      })
    }
  })

  window.addEventListener('pagehide', flushWatchSessions)

  // A color swatch change rebuilds the gallery with different media. Close out
  // and emit the watch time for the outgoing color's PDP and Gallery videos
  // before the new media takes over. Gated on isColorChange so size changes —
  // which keep the same media elements playing — don't cut a session short.
  document.addEventListener('variant-picker:change', function (event) {
    if (!event || !event.detail || event.detail.isColorChange !== true) return
    flushAndResetSessions()
  })

  // Expose globally for other scripts. `pauseAllVimeo` is preserved as an
  // alias for backwards compatibility with any callers from before the
  // Vimeo → MP4 migration.
  window.pauseAllVideos = pauseAll
  window.pauseAllVimeo = pauseAll
})()
